import React, { useEffect, useState } from "react";
import { BsList } from "react-icons/bs";
import { IoIosClose } from "react-icons/io";
import { useLanguage } from "../context/LanguageContext";
import translations from "../i18n/translations";

const SECTION_IDS = ["about", "experience", "projects", "skills", "contact"];

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightToggle, setHighlightToggle] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const { language, toggleLanguage } = useLanguage();

  const t = translations[language];

  useEffect(() => {
    const timer = setTimeout(() => setHighlightToggle(false), 3500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );

    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const toggleButtonClasses =
    "relative inline-flex items-center justify-center rounded-full border border-coding-color px-3 py-1 text-xs font-semibold text-gray-light bg-gray-900/60 shadow-[0_0_12px_rgba(34,211,238,0.45)] backdrop-blur-sm transition hover:bg-coding-color hover:text-gray-900 hover:shadow-[0_0_18px_rgba(34,211,238,0.8)]" +
    (highlightToggle ? " animate-pulse-soft" : "");

  const toggleMenu = () => setIsOpen((prev) => !prev);

  const closeMenu = () => setIsOpen(false);

  const linkClasses = (id) =>
    `hover:text-white transition-colors ${
      activeSection === id ? "text-coding-color" : ""
    }`;

  return (
    <header
      className={`nav sticky top-0 z-50 bg-gray-900/90 backdrop-blur transition-shadow duration-300 ${
        isScrolled ? "shadow-lg shadow-black/30" : ""
      }`}
    >
      <nav className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
        <a
          href="#top"
          className="logo font-main-font text-2xl font-bold tracking-wide text-coding-color transition-transform hover:scale-105"
        >
          JR
        </a>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleLanguage}
            aria-label={t.common.languageToggleLabel}
            className={toggleButtonClasses}
          >
            {language === "en"
              ? t.common.languageShort.fr
              : t.common.languageShort.en}
          </button>

          <div className="humbugger-container md:hidden">
            <button
              className="rounded-md text-white focus:outline-none"
              onClick={toggleMenu}
            >
              <span className="sr-only">Open main menu</span>
              <BsList className="h-6 w-6 text-coding-color" />
            </button>
          </div>
        </div>

        <div
          className={`navigation absolute left-0 right-0 top-full overflow-hidden bg-gray-900 transition-[max-height] duration-300 ease-in-out md:static md:!max-h-none md:overflow-visible md:bg-transparent ${
            isOpen ? "max-h-96" : "max-h-0"
          }`}
        >
          <ul className="flex flex-col items-center gap-4 px-4 py-4 font-coding-font text-lg text-gray-light md:flex-row md:gap-8 md:px-0 md:py-0">
            <li className="flex w-full justify-end md:hidden">
              <button
                aria-label="Close menu"
                className="text-3xl text-gray-light"
                onClick={closeMenu}
              >
                <IoIosClose />
              </button>
            </li>
            <li className="link">
              <a
                className={linkClasses("about")}
                href="#about"
                onClick={closeMenu}
              >
                {t.navbar.about}
              </a>
            </li>
            <li className="link">
              <a
                className={linkClasses("experience")}
                href="#experience"
                onClick={closeMenu}
              >
                {t.navbar.experience}
              </a>
            </li>
            <li className="link">
              <a
                className={linkClasses("projects")}
                href="#projects"
                onClick={closeMenu}
              >
                {t.navbar.projects}
              </a>
            </li>
            <li className="link">
              <a
                className={linkClasses("skills")}
                href="#skills"
                onClick={closeMenu}
              >
                {t.navbar.skills}
              </a>
            </li>
            <li className="link">
              <a
                className={linkClasses("contact")}
                href="#contact"
                onClick={closeMenu}
              >
                {t.navbar.contact}
              </a>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
}

export default Navbar;
